import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { Reveal, SectionHeader } from './Reveal';

const EASE = [0.16, 1, 0.3, 1];

const QUESTIONS = [
    'Current business continuity plans exist for every critical function',
    'IT disaster recovery has been tested in the last 12 months',
    'A business impact analysis sets your recovery priorities',
    'Leadership has rehearsed a crisis scenario in the last year',
    'Critical third-party dependencies are mapped, with alternates',
];

const OPTIONS = ['Not in place', 'Documented', 'Implemented', 'Tested & proven'];

const BANDS = [
    { min: 85, label: 'Proven', tone: 'bg-deepteal', advice: 'Strong posture. Focus on keeping evidence current — rotate scenarios and re-test annually so readiness does not decay.' },
    { min: 65, label: 'Established', tone: 'bg-deepteal', advice: 'Real capability with meaningful gaps. Prioritize testing what you have documented — evidence is the difference between Established and Proven.' },
    { min: 40, label: 'Developing', tone: 'bg-amber-500', advice: 'Foundations exist but recovery is unproven. A focused program — BIA, recovery testing, and one leadership exercise — would transform your posture within two quarters.' },
    { min: 0, label: 'Exposed', tone: 'bg-red-500', advice: 'Your organization is carrying more risk than it needs to. Start with a business impact analysis and one facilitated tabletop exercise — the highest-value first steps.' },
];

export const ReadinessCalculator = () => {
    const [answers, setAnswers] = useState([0, 0, 0, 0, 0]);
    const score = Math.round((answers.reduce((a, b) => a + b, 0) / (QUESTIONS.length * 3)) * 100);
    const band = BANDS.find((b) => score >= b.min);

    return (
        <section className="border-t border-slate-700 bg-white py-20 sm:py-28" data-testid="readiness-calculator">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <SectionHeader
                    dark={false}
                    overline="Self-assessment"
                    title="Score your readiness in ninety seconds."
                    description="Rate five capabilities honestly. The score is indicative, not audit-grade — but it maps closely to what our first assessments find."
                />
                <Reveal delay={0.1}>
                    <div className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-12">
                        <div className="rounded-2xl border border-slate-700 bg-white p-7 shadow-sm sm:p-9 lg:col-span-7">
                            {QUESTIONS.map((q, qi) => (
                                <div key={q} className={qi > 0 ? 'mt-7 border-t border-slate-700 pt-7' : ''} data-testid={`readiness-question-${qi}`}>
                                    <p className="text-sm font-semibold text-slate-100">{q}</p>
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        {OPTIONS.map((opt, oi) => (
                                            <button
                                                key={opt}
                                                type="button"
                                                data-testid={`readiness-option-${qi}-${oi}`}
                                                onClick={() => setAnswers(answers.map((a, i) => (i === qi ? oi : a)))}
                                                className={`rounded-full border px-4 py-2 text-xs font-medium transition-all duration-200 ${answers[qi] === oi ? 'border-deepteal/50 bg-deepteal/10 text-deepteal' : 'border-slate-700 text-slate-400 hover:border-slate-600 hover:text-slate-200'}`}
                                            >
                                                {opt}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="lg:col-span-5">
                            <div className="sticky top-28 rounded-2xl border border-deepteal/25 bg-deepteal/5 p-7 sm:p-9">
                                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Your readiness score</p>
                                <p className="mt-4 font-display text-5xl sm:text-6xl font-extrabold tracking-tight text-slate-50" data-testid="readiness-score">
                                    {score}<span className="text-2xl text-slate-300">%</span>
                                </p>
                                <div className="mt-5 h-2 overflow-hidden rounded-full bg-slate-700">
                                    <motion.div
                                        animate={{ width: `${score}%` }}
                                        transition={{ duration: 0.7, ease: EASE }}
                                        className={`h-full rounded-full ${band.tone}`}
                                    />
                                </div>
                                <p className="mt-4 font-display text-xl font-bold text-deepteal" data-testid="readiness-band">{band.label}</p>
                                <p className="mt-3 text-sm leading-relaxed text-slate-300">{band.advice}</p>
                                <Link
                                    to="/contact"
                                    data-testid="readiness-cta-button"
                                    className="mt-7 inline-flex items-center gap-2 rounded-full bg-glow px-6 py-3.5 text-sm font-semibold text-ink transition-transform duration-200 hover:scale-105 hover:shadow-lg hover:shadow-glow/25"
                                >
                                    Book a consultation to close the gap <ArrowUpRight size={15} />
                                </Link>
                            </div>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};
